// Last rendered images survive live/cached control swaps, including signed S3 URLs.
// Memory only: signed URLs must not be persisted in localStorage.
const displayedAvatars = new Map<string, string>();
export function readDisplayedAvatar(kind: "user" | "organization") {
  return displayedAvatars.get(kind);
}
export function rememberDisplayedAvatar(
  kind: "user" | "organization",
  src?: string | null,
) {
  if (src) displayedAvatars.set(kind, src);
  else displayedAvatars.delete(kind);
}

// Presentation only: never use this snapshot for authentication or authorization.
export type AccountRailSnapshot = {
  insights: boolean;
  upgrade: boolean;
  avatar: string | null;
  organizationLogo: string | null;
};
const KEY = "portal-account-rail-v1";
export function readAccountRail(): AccountRailSnapshot | null {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) || "null");
    if (
      !value ||
      typeof value.insights !== "boolean" ||
      typeof value.upgrade !== "boolean"
    )
      return null;
    return {
      insights: value.insights,
      upgrade: value.upgrade,
      avatar: localAvatar(value.avatar),
      organizationLogo: localAvatar(value.organizationLogo),
    };
  } catch {
    return null;
  }
}
export function localAvatar(value: unknown): string | null {
  return typeof value === "string" && /^\/avatars\/[a-zA-Z0-9_.-]+$/.test(value)
    ? value
    : null;
}
export function writeAccountRail(value: AccountRailSnapshot | null) {
  try {
    const root = document.documentElement;
    if (value) {
      root.dataset.accountRail = "cached";
      root.dataset.railInsights = String(value.insights);
      root.dataset.railUpgrade = String(value.upgrade);
    } else {
      displayedAvatars.clear();
      delete root.dataset.accountRail;
    }
    for (const [path, property, attribute] of [
      [value?.avatar, "--rail-user-image", "railUserImage"],
      [value?.organizationLogo, "--rail-org-image", "railOrgImage"],
    ] as const) {
      const safePath = localAvatar(path);
      if (safePath) {
        root.style.setProperty(property, `url("${safePath}")`);
        root.dataset[attribute] = "true";
      } else {
        root.style.removeProperty(property);
        delete root.dataset[attribute];
      }
    }
    const serialized = value ? JSON.stringify(value) : null;
    if (localStorage.getItem(KEY) === serialized) return;
    if (serialized) localStorage.setItem(KEY, serialized);
    else localStorage.removeItem(KEY);
    window.dispatchEvent(new Event("portal-account-rail-changed"));
  } catch {
    /* Storage is optional. */
  }
}
